import type { ChatScript, ChatStep, Checkpoint, ScriptMeta } from './types';
import type { TopicDocument } from '@/cms/topics';
import type { Content } from '@prismicio/client';
import { discoveryEntries } from './discovery';

/** Every slice type the chat can be handed, whether or not it renders one. */
type TopicSlice = TopicDocument['data']['slices'][number];

export type ScriptOptions = {
  /**
   * Emit `activity` steps. On by default now the cards exist; passing `false`
   * skips an activity slice exactly like a disabled one, so it does not count
   * toward the module's slice total.
   */
  activities?: boolean;
};

/** `question_key` keeps analytics comparable across languages; the slice id is the fallback. */
function questionId(slice: { id: string; primary: { question_key?: Content.QuizSlice['primary']['question_key'] } }) {
  const key = slice.primary.question_key?.trim();

  return key !== undefined && key !== '' ? key : slice.id;
}

/**
 * Turns one slice into a step, or `null` when the app skips it.
 *
 * `general_content` never appears on a topic, and `scrollytelling` has no chat
 * form. Outside Production, `scrollytelling-bridge.server.ts` replaces it with
 * `activity` slices before the topic gets here. A skipped slice does not count
 * toward progress, the same treatment `enabled: false` gets in the current app.
 */
function stepFromSlice(slice: TopicSlice, dividerIndex: number, options: ScriptOptions): ChatStep | null {
  switch (slice.slice_type) {
    case 'content_bubble':
      return { kind: 'coach', id: slice.id, text: slice.primary.content_bubble };

    case 'image':
      return {
        kind: 'image',
        id: slice.id,
        image: {
          image: slice.primary.image,
          caption: slice.primary.image_caption,
          description: slice.primary.image_description,
        },
      };

    case 'image_carousel':
      return {
        kind: 'carousel',
        id: slice.id,
        images: slice.primary.images.map((item) => ({
          image: item.image,
          caption: item.image_caption,
          description: item.image_description,
        })),
      };

    case 'video':
      return {
        kind: 'video',
        id: slice.id,
        title: slice.primary.title,
        subtitle: slice.primary.video_subtitle,
        poster: slice.primary.video_image,
        embedLink: slice.primary.embed_link,
        transcript: slice.primary.video_transcript,
      };

    case 'quiz':
      return {
        kind: 'quiz',
        id: slice.id,
        questionId: questionId(slice),
        question: slice.primary.question,
        acceptedAnswer: slice.primary.accepted_answer,
        answers: slice.primary.answers.map((answer) => ({
          label: answer.answer_label,
          feedback: answer.answer_feedback,
        })),
      };

    case 'text_checklist':
      return {
        kind: 'checklist',
        id: slice.id,
        questionId: questionId(slice),
        question: slice.primary.question,
        responseText: slice.primary.response_text,
        // Both feedback fields are absent on slices saved before they existed.
        feedbackWrong: slice.primary.feedback_wrong ?? [],
        items: slice.primary.items.map((item) => ({
          label: item.item,
          correct: item.is_item_correct === true,
          feedback: item.feedback ?? [],
        })),
      };

    case 'user_interaction':
      return {
        kind: 'interaction',
        id: slice.id,
        questionId: questionId(slice),
        question: slice.primary.question,
        options: slice.primary.options.map((option) => ({
          label: option.option_text,
          feedback: option.option_feedback,
        })),
      };

    case 'discovery':
      return {
        kind: 'discovery',
        id: slice.id,
        questionId: questionId(slice),
        intro: slice.primary.intro,
        prompt: slice.primary.prompt,
        promptAgain: slice.primary.prompt_again,
        progressLabel: slice.primary.progress_label,
        outro: slice.primary.outro,
        entries: discoveryEntries(slice),
      };

    case 'section_divider':
      return { kind: 'divider', id: slice.id, index: dividerIndex, title: slice.primary.title };

    case 'key_takeaways':
      return {
        kind: 'takeaways',
        id: slice.id,
        heading: slice.primary.heading,
        points: slice.primary.points.map((point) => point.point),
      };

    case 'activity':
      return options.activities === false
        ? null
        : { kind: 'activity', id: slice.id, questionId: questionId(slice), slice };

    case 'scrollytelling':
      return null;
  }
}

/**
 * The playable script for a module: the enabled slices the app renders, in
 * order, plus the checkpoint model the module header draws.
 *
 * A topic with no `section_divider` gets a single checkpoint named after the
 * module, so the header always has something honest to show.
 */
export function slicesToScript(slices: Array<TopicSlice>, meta: ScriptMeta, options: ScriptOptions = {}): ChatScript {
  const steps: Array<ChatStep> = [];
  const checkpoints: Array<Checkpoint> = [];

  for (const slice of slices) {
    // `general_content` is the only topic slice without an `enabled` flag, and
    // it never appears on a learning topic.
    if ('enabled' in slice.primary && slice.primary.enabled !== true) {
      continue;
    }

    const step = stepFromSlice(slice, checkpoints.length + 1, options);

    if (!step) {
      continue;
    }

    if (step.kind === 'divider') {
      // A blank divider title falls back to the module's, in the header and the thread alike.
      const title = step.title?.trim() || meta.title;

      checkpoints.push({ index: step.index, title });
      steps.push({ ...step, title });
      continue;
    }

    steps.push(step);
  }

  return {
    meta,
    steps,
    checkpoints: checkpoints.length > 0 ? checkpoints : [{ index: 1, title: meta.title }],
  };
}
